"use client";
import { useAppContext } from "@/providers/AppProvider";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";

const messages = [
    "Just a sec — organizing creativity and code...",
    "Loading the magic... hang in there!",
    "Polishing up the portfolio for your eyes only...",
    "This won’t take long — preparing awesome stuff...",
    "Loading... because good things take time.",
];

export default function LoadingScreen() {
    const { loading } = useAppContext();
    const [messageIndex, setMessageIndex] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setMessageIndex((prev) => (prev + 1) % messages.length);
        }, 3000);
        return () => clearInterval(interval);
    }, []);

    return (
        <AnimatePresence>
            {loading && (
                <motion.div
                    className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-background-color p-4 text-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0, transition: { duration: 0.6 } }}
                >
                    {/* Background pulse animation */}
                    <motion.div
                        className="absolute inset-0 bg-gradient-to-tr from-primary/10 to-primary/5"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.5 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.8 }}
                    />
                    {/* Animated text */}
                    <motion.h2
                        key={messageIndex}
                        className="z-10 mb-4 max-w-[90%] text-2xl font-bold text-primary md:text-3xl"
                        initial={{ opacity: 0, y: 20, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -20, scale: 0.98 }}
                        transition={{
                            duration: 0.6,
                            ease: "easeInOut",
                        }}
                    >
                        {messages[messageIndex]}
                    </motion.h2>
                    {/* Loading dots */}
                    <motion.div
                        className="z-10 mt-6 flex h-2 w-10 gap-2"
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        variants={{
                            hidden: { opacity: 0 },
                            visible: {
                                opacity: 1,
                                transition: {
                                    staggerChildren: 0.2,
                                    repeat: Infinity,
                                },
                            },
                        }}
                    >
                        {[0, 1, 2].map((i) => (
                            <motion.div
                                key={i}
                                className="h-2 w-2 rounded-full bg-primary"
                                variants={{
                                    hidden: { scale: 0.6, opacity: 0.3 },
                                    visible: {
                                        scale: [1, 1.4, 1],
                                        opacity: [0.3, 1, 0.3],
                                        transition: {
                                            duration: 1.2,
                                            repeat: Infinity,
                                            repeatType: "loop",
                                            delay: i * 0.2,
                                        },
                                    },
                                }}
                            />
                        ))}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
